<!-- Copyright 2020 Adobe. All rights reserved.
This file is licensed to you under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License. You may obtain a copy
of the License at http://www.apache.org/licenses/LICENSE-2.0
Unless required by applicable law or agreed to in writing, software distributed under
the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
OF ANY KIND, either express or implied. See the License for the specific language
governing permissions and limitations under the License. -->

import {Layout} from '@react-spectrum/docs';
export default Layout;

import docs from 'docs:@react-spectrum/slider';
import {HeaderInfo, PropTable} from '@react-spectrum/docs';
import packageData from '@react-spectrum/slider/package.json';

```jsx import
import {Flex} from '@react-spectrum/layout';
import {Slider} from '@react-spectrum/slider';
```

---
category: Forms
---

# Slider

<p>{docs.exports.Slider.description}</p>

<HeaderInfo
  packageData={packageData}
  componentNames={['Slider']}
  sourceData={[{type: 'Spectrum', url: 'https://spectrum.adobe.com/page/slider/'}]} />

## Example

```tsx example
<Slider label="Cookies to buy" defaultValue={12} />
```

## Value

Sliders are controlled with the `value` and `defaultValue` props which must fall between
the minimum and maximum values which default of 0 and 100, respectively.

```tsx example
function Example() {
  let [value, setValue] = React.useState(25);
  return (
    <Flex gap="size-150">
      <Slider
        label="Cookies to buy (Uncontrolled)"
        defaultValue={25} />
      <Slider
        label="Cookies to buy (Controlled)"
        value={value}
        onChange={setValue} />
    </Flex>
  );
}
```

Alternatively, a different scale can be used by setting the `minValue` and `maxValue` props.

```tsx example
<Slider
  label="Cookies to buy"
  minValue={50}
  maxValue={150}
  defaultValue={100} />
```

Values are formatted as plainly by default, but this can be modified by using the `formatOptions` prop to specify a different format.
`formatOptions` is compatible with the option parameter of [Intl.NumberFormat](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/NumberFormat) and is applied based on the current locale.

```tsx example
<Slider
  label="Currency"
  formatOptions={{style: 'currency', currency: 'JPY'}}
  defaultValue={60} />
```

### Accessibility

Depending on the visualization being used (i.e. depending on the `showValueLabel` prop), a `label`, `aria-label`, or `aria-labelledby` prop is required.

### Internationalization

To internationalize a Slider, a localized string should be passed to the `label` prop, `aria-label` prop, or value of the associated `aria-labelledby` element.

For languages that are read right-to-left (e.g. Hebrew and Arabic), the layout of the slider is automatically flipped. In addition,
Slider automatically uses the current locale to format the value label.

## Labeling
Value labels are shown above the Slider by default, but they can also be moved to the side or hidden as needed.
The label text should be in sentence case.

```tsx example
<Flex direction="column" maxWidth="size-3000" gap="size-300">
  <Slider label="Cookies to buy" defaultValue={25} />
  <Slider label="Donuts to buy" labelPosition="side" defaultValue={25} />
  <Slider label="Pastries to buy" showValueLabel={false} defaultValue={25} />
</Flex>
```

The value label can be updated via the `valueLabel` prop in cases where showing a different scale makes sense. The `valueLabel`
should be appropriately translated.
```tsx example
function Example() {
  let [value, setValue] = React.useState(25);
  return <Slider label="Cookies to buy" marginBottom="size-300" value={value} onChange={setValue} valueLabel={`${value} of 100`} />
}
```

By default, the value label is formatted as a plain number. This can be customized using the following props.

* `showValueLabel`: Allows you to display or hide the value label.
* `formatOptions`: Allows you to customize the format of the value.
* `valueLabel`: Allows you to customize the value label to any string.

```tsx example
<Flex direction="column" maxWidth="size-3000" gap="size-300">
  <Slider
    label="Cookies to buy"
    showValueLabel={false}
    defaultValue={90} />

  <Slider
    label="Donuts to buy"
    maxValue={60}
    valueLabel="54 of 60 Donuts"
    defaultValue={54} />

  <Slider
    label="Percent donus eaten"
    maxValue={1}
    formatOptions={{style: 'percent', minimumFractionDigits: 2}}
    defaultValue={0.8912} />
</Flex>
```
## Props

<PropTable component={docs.exports.Slider} links={docs.links} />

## Visual options

### Fill
[View guidelines](https://spectrum.adobe.com/page/slider/#Fill)

```tsx example
<Flex direction="column" gap="size-300">
  <Slider label="Opacity" maxValue={1} formatOptions={{style: 'percent'}} defaultValue={0.9} step={0.01} isFilled />
  <Slider label="Exposure" minValue={-5} maxValue={5} defaultValue={1.83} formatOptions={{signDisplay: 'always'}} step={0.01} fillOffset={0} isFilled />
</Flex>
```

### Disabled
[View guidelines](https://spectrum.adobe.com/page/slider/#Semantic-colors)

```tsx example
<Slider label="Cookies to share" defaultValue={25} isDisabled />
```
